/**
 * modelManager.js - Model loading, parsing, and GLTF handling
 *
 * Handles:
 * - GLTF model loading
 * - Model setup and parsing (groups, sides, materials)
 * - Model reloading and cleanup
 * - Shadow mesh handling
 * - Mesh registry setup
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as state from './state.js';
import { getLabelText, showLoader, hideLoaderSmooth } from './utils.js';
import { storeInitialView, applyInitialZoom, fitCameraToObject, adjustCameraForDevice, adjustFogToModel, createFakeShadow, updateGrid, requestRender } from './sceneManager.js';
import { updateZoomDisplay, setZoomValueEl } from './sceneManager.js';
import { invalidateMeshCache } from './decalManager.js';
import { buildFeatureTogglesUI } from './markerManager.js';

// ===== MODEL LOADING =====

/**
 * Adds a cache-busting timestamp to a URL
 * @param {string} url - The URL to add the timestamp to
 * @returns {string} URL with timestamp parameter
 */
function addCacheBustingTimestamp(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
}

export function loadModel(modelUrl, manager, dracoLoader, onLoad, onError) {
    // Add timestamp to prevent aggressive caching
    const urlWithTimestamp = addCacheBustingTimestamp(modelUrl);

    new GLTFLoader(manager)
        .setDRACOLoader(dracoLoader)
        .load(urlWithTimestamp, onLoad, undefined, onError);
}

export function onModelLoad(gltf, updateGrid, buildUIFromMarkers, rebuildOccludeTargets, updateMarkers) {
    setupModel(gltf, buildUIFromMarkers, rebuildOccludeTargets, updateMarkers);
    updateGrid();
    createFakeShadow(gltf.scene);

    // Match the rendering behavior of onModelReloaded to ensure consistent shadow appearance
    // Clear renderer and force multiple renders to ensure shadow is properly displayed
    state.renderer.clear(true, true, true);
    requestRender();

    // Additional render after a short delay to ensure shadow texture is fully loaded
    setTimeout(() => {
        requestRender();
        updateMarkers(true);
    }, 200);
}

export function onModelReloaded(gltf, initSizeSelectorCallback, updateMarkers, buildUIFromMarkers, rebuildOccludeTargets) {
    setupModel(gltf, buildUIFromMarkers, rebuildOccludeTargets, updateMarkers);
    updateGrid();
    createFakeShadow(gltf.scene);

    storeInitialView();
    state.setZoomPercent(state.initialZoom);
    applyInitialZoom();
    updateZoomDisplay();

    if (initSizeSelectorCallback && typeof initSizeSelectorCallback === 'function') {
        initSizeSelectorCallback();
    }

    state.setIsLoading(false);
    state.renderer.clear(true, true, true);
    requestRender();
    setTimeout(() => updateMarkers(true), 200);
}

// ===== MODEL SETUP =====

export function setupModel(gltf, buildUIFromMarkers, rebuildOccludeTargets, updateMarkers) {
    try {
    if (state.overlayEl) state.overlayEl.querySelectorAll('.marker').forEach(m => m.remove());

    state.scene.add(gltf.scene);
    state.setModelRoot(gltf.scene);

    const getConfigDataFromMatName = n => {
        if (!n) return {group: 'default', side: null};
        // Remove Blender export suffix (.001, .002, etc.) and protection markers
        let cleanName = n.replace(/\.\d+$/, '');
        cleanName = cleanName.replace(/_(noDecals?|protected)/gi, '');
        const low = cleanName.toLowerCase();

        if (low.includes('configurator')) {
            if (low.startsWith('configurator-')) {
                const base = low.split('configurator-')[1];
                const parts = base.split('-');

                if (parts.length >= 2) {
                    const group = parts.length > 2 ? parts.slice(0, parts.length - 1).join('-') : parts[0];
                    const side = parts[parts.length - 1];
                    if (group === side || !group) return {group: 'default', side};
                    return {group, side};
                }

                return {group: 'default', side: parts[0] || 'surface'};
            }

            if (low.startsWith('configurator-mat-')) {
                const side = low.split('configurator-mat-')[1];
                return {group: 'default', side: side || 'surface'};
            }

            return {group: 'default', side: 'surface'};
        }

        return {group: 'default', side: null};
    };

    // Schaltbare Ausstattungsteile (z.B. Geländer, Terrasse): Node-Name "configurator-<key>"
    // (mehrere Objekte über Blender-Suffix ".001" usw. möglich). Getrennt von getConfigDataFromMatName,
    // da hier Objekt-/Node-Namen ausgewertet werden, nicht Material-Namen.
    // WICHTIG: Der GLTFLoader saniert Node-Namen für Animation-Bindings und entfernt dabei Punkte -
    // aus "configurator-railing.001" wird beim Laden "configurator-railing001" (ohne Punkt). Anders
    // als bei Materialnamen (unverändert) muss der Suffix hier also auch ohne Punkt erkannt werden.
    const getFeatureGroupFromNodeName = n => {
        if (!n) return null;
        const low = n.toLowerCase();
        if (!low.startsWith('configurator-')) return null;
        const key = low.slice('configurator-'.length).replace(/\.?\d+$/, '');
        return key || null;
    };

    /**
     * Schutz gegen Parenting-Fehler in der 3D-Datei: Ein Objekt, das selbst als "configurator-<key>"
     * benannt ist, aber ANDERE configurator-getaggte Objekte als Kind-Hierarchie enthält, ist mit
     * hoher Wahrscheinlichkeit kein einzelnes Bauteil, sondern ein Wrapper/Root, an dem versehentlich
     * die restliche Szene hängt (z.B. weil beim Zusammenparenten mehrerer Objekte in Blender aus
     * Versehen ein echtes Bauteil statt eines leeren Root-Objekts als letztes/aktives ausgewählt
     * wurde). Ein echtes Einzelteil (Geländerstab, Terrassenplatte, ...) enthält nie ein weiteres
     * configurator-Objekt in seiner eigenen Unter-Hierarchie. Toggle auf so einem Wrapper würde
     * beim Ausblenden fast das ganze Modell mitreißen - deshalb wird er aus seiner Gruppe entfernt.
     * Seine "echten" Geschwister/Kinder bleiben davon unberührt und bleiben einzeln schaltbar.
     */
    function excludeFeatureWrapperNodes() {
        const tagged = [];
        state.featureGroups.forEach((nodes, key) => {
            nodes.forEach(node => tagged.push({ node, key }));
        });

        const isDescendantOf = (node, ancestor) => {
            let p = node.parent;
            while (p) {
                if (p === ancestor) return true;
                p = p.parent;
            }
            return false;
        };

        tagged.forEach(({ node, key }) => {
            const isWrapper = tagged.some(other => other.node !== node && isDescendantOf(other.node, node));
            if (isWrapper) {
                console.warn(`[3D Configurator] "${node.name}" enthält andere configurator-Objekte in seiner Kind-Hierarchie und wird deshalb vom Sichtbarkeits-Toggle "${key}" ausgeschlossen - vermutlich ein Parenting-Fehler in der 3D-Datei (dieses Objekt hängt fälschlich als Elternteil an der übrigen Szene).`);
                state.featureGroups.get(key).delete(node);
            }
        });
    }

    function parseMarkerName(name) {
        if (!name || typeof name !== "string") return null;

        // Remove Blender export suffix (.001, .002, etc.) and protection markers
        let cleanName = name.replace(/\.\d+$/, '');
        cleanName = cleanName.replace(/_(noDecals?|protected)/gi, '');
        const low = cleanName.toLowerCase();
        if (!low.endsWith("-marker")) return null;

        const base = low.replace(/^configurator-/, "").replace(/-marker$/, "");
        const parts = base.split("-");
        if (parts.length >= 2) {
            const side = parts.pop();
            const group = parts.join("-");
            return {group, side};
        }

        return {group: "default", side: base};
    }


    const helperGeo = new THREE.SphereGeometry(0.01, 8, 8);
    const helperMat = new THREE.MeshBasicMaterial({color: 0xffffff, transparent: true, opacity: 0.001});

    const groups = new Map();
    const newAllMeshes = [];
    let shadowMeshCount = 0;

    // Debug: Log model structure on iOS
    if (window.DEBUG_IOS_ERRORS) {
        console.log('[DEBUG] Starting model traverse...');
        let debugMeshCount = 0;
        let debugMatNames = [];
        gltf.scene.traverse(c => {
            if (c.isMesh) {
                debugMeshCount++;
                const mats = Array.isArray(c.material) ? c.material : [c.material];
                mats.forEach(m => {
                    if (m.name && !debugMatNames.includes(m.name)) {
                        debugMatNames.push(m.name);
                    }
                });
            }
        });
        console.log('[DEBUG] Total meshes:', debugMeshCount);
        console.log('[DEBUG] Material names:', debugMatNames.join(', '));
        // Alert removed to avoid intrusive popups on devices
    }

    gltf.scene.traverse(c => {
        // Schaltbare Ausstattungsteile: gilt für JEDEN Node-Typ, nicht nur Meshes -
        // in Blender werden mehrteilige Features oft unter einem benannten Empty/Group
        // gruppiert statt jedes Einzelteil selbst umzubenennen. Object3D.visible kaskadiert
        // ohnehin auf alle Kind-Objekte, das Toggle auf dem Group-Node reicht also aus.
        const featureKey = getFeatureGroupFromNodeName(c.name);
        if (featureKey) {
            if (!state.featureGroups.has(featureKey)) state.featureGroups.set(featureKey, new Set());
            state.featureGroups.get(featureKey).add(c);
        }

        if (c.isMesh) {
            // Check polygon count for debugging high-poly meshes
            if (c.geometry) {
                const polyCount = c.geometry.index
                    ? c.geometry.index.count / 3
                    : (c.geometry.attributes.position?.count || 0) / 3;
                if (polyCount > 150000) {
                    console.warn(`[3D Configurator] High polygon mesh detected: "${c.name || 'unnamed'}" has ${Math.round(polyCount).toLocaleString()} polygons`);
                }
            }

            c.castShadow = c.receiveShadow = true;
            newAllMeshes.push(c);
            shadowMeshCount++;

            const origMat = Array.isArray(c.material)
                ? c.material.map(m => m.clone())
                : [c.material.clone()];
            state.originalMaterials.set(c.uuid, origMat);

            const mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(m => {
                // Speichere ALLE Original-Materialwerte beim Modell-Laden
                if (!m.userData.originalColor) {
                    m.userData.originalColor = m.color.clone();
                }
                if (m.userData.originalMetalness === undefined) {
                    m.userData.originalMetalness = m.metalness;
                }
                if (m.userData.originalRoughness === undefined) {
                    m.userData.originalRoughness = m.roughness;
                }
                if (m.userData.originalOpacity === undefined) {
                    m.userData.originalOpacity = m.opacity;
                }

                // Reduziere HDR-Reflexionen für Glas-Materialien
                const isGlass = m.name?.toLowerCase().includes('glas');
                if (isGlass) {
                    // Reduziere Environment Map Intensität sehr leicht
                    m.envMapIntensity = 0.7;
                    // Erhöhe Roughness minimal für leicht weichere Reflexionen
                    // WICHTIG: Ändere nicht m.roughness direkt, da dies bereits als Original gespeichert wurde
                }

                const {group, side} = getConfigDataFromMatName(m.name);
                if (!groups.has(group)) {
                    groups.set(group, {mats: new Set(), sides: new Map(), color: null, keepAlpha: false});
                }
                const groupData = groups.get(group);

                if (side) {
                    if (!state.sideMats.has(group)) state.sideMats.set(group, new Map());
                    if (!state.sideMats.get(group).has(side)) state.sideMats.get(group).set(side, new Set());
                    state.sideMats.get(group).get(side).add(m);

                    if (!state.sideMeshes.has(group)) state.sideMeshes.set(group, new Map());
                    if (!state.sideMeshes.get(group).has(side)) state.sideMeshes.get(group).set(side, new Set());
                    state.sideMeshes.get(group).get(side).add(c);
                }

                groupData.mats.add(m);
                if (side) {
                    if (!groupData.sides.has(side)) {
                        groupData.sides.set(side, {mats: new Set(), meshes: new Set(), color: null, keepAlpha: false});
                    }
                    const sideData = groupData.sides.get(side);
                    sideData.mats.add(m);
                    sideData.meshes.add(c);

                    // Setze keepAlpha Flag für Glas-Materialien
                    if (isGlass) {
                        sideData.keepAlpha = true;
                        groupData.keepAlpha = true;
                    }
                }
            });
        }
    });

    state.setAllMeshes(newAllMeshes);

    excludeFeatureWrapperNodes();

    // MARKER DEAKTIVIERT FÜR PERFORMANCE - Kann später wieder aktiviert werden
    const SHOW_MARKERS = false;

    if (SHOW_MARKERS) {
        gltf.scene.traverse(c => {
            const parsed = parseMarkerName(c.name);
            if (!parsed) return;
            const {group, side} = parsed;

            const groupName = groups.has(group) ? group : "default";
            const groupData = groups.get(groupName);

            if (groupData?.sides.has(side)) {
                const helper = new THREE.Mesh(helperGeo, helperMat);
                c.add(helper);
                state.markerHelpers.push(helper);

                const el = document.createElement("div");
                el.className = "marker";
                el.dataset.side = side;
                el.dataset.group = groupName;

                const dot = document.createElement("div");
                dot.className = "dot";
                const label = document.createElement("div");
                label.className = "label";
                label.textContent = getLabelText(groupName, side);
                el.appendChild(dot);
                el.appendChild(label);
                state.overlayEl.appendChild(el);

                el.addEventListener("click", ev => {
                    ev.stopPropagation();
                    const key = `${groupName}:${side}`;
                });

                state.markers.set(`${groupName}:${side}`, {empty: c, helper, el, group: groupName, side});
            }
        });
    }

    const box = new THREE.Box3().setFromObject(gltf.scene);
    const center = box.getCenter(new THREE.Vector3());
    gltf.scene.position.sub(center);
    fitCameraToObject(gltf.scene);

    if (window.innerWidth >= 768) {
        const shift = 150;
        const aspect = state.camera.aspect;
        const vFOV = THREE.MathUtils.degToRad(state.camera.fov);
        const dist = state.camera.position.distanceTo(state.controls.target);
        const viewHeight = 2 * Math.tan(vFOV / 2) * dist;
        const viewWidth = viewHeight * aspect;
        const worldShift = (shift / state.renderer.domElement.clientWidth) * viewWidth;

        state.camera.position.x -= worldShift;
        state.controls.target.x -= worldShift;
        state.controls.update();
    }

    storeInitialView();
    adjustCameraForDevice();
    adjustFogToModel(gltf.scene);

    const bbox = new THREE.Box3().setFromObject(gltf.scene);
    const minY = bbox.min.y;

    if (state.floor) state.floor.position.y = minY - 0.01;
    if (state.grid) state.grid.position.y = state.floor.position.y + 0.02;
    if (state.fadePlane) state.fadePlane.position.y = minY + 0.01;
    state.setZoomPercent(state.initialZoom);
    applyInitialZoom();
    updateZoomDisplay();
    state.setActiveSide(null);

    if (window._configGroups) {
        window._configGroups.clear();
    }
    window._configGroups = groups;

    buildUIFromMarkers(groups);
    buildFeatureTogglesUI(state.featureGroups);

    rebuildOccludeTargets();

    updateGrid();

    requestAnimationFrame(() => {
        updateMarkers(true);
        requestRender();
    });
    } catch (error) {
        console.error('[setupModel] Error during model setup:', error);
        console.error('[setupModel] Stack:', error.stack);
        // Debug: Show error on iOS where console is hard to access
        if (window.DEBUG_IOS_ERRORS) {
            console.error('[setupModel] Error: ' + error.message + '\n\nStack: ' + error.stack?.substring(0, 500));
        }
        // Don't re-throw to prevent page reload on iOS
        state.setIsLoading(false);
    }
}

// ===== MODEL RELOAD =====

export function reloadModel(newModelUrl, manager, dracoLoader, loaderEl, containerEl, onModelReloaded) {
    if (state.isLoading) {
        return;
    }

    state.setIsLoading(true);

    // Reset radio initialization flag so radios get properly updated on model change
    window._radioInitialized = false;

    const panelContainer = document.querySelector('.configuratorPanel');
    if (panelContainer) {
        panelContainer.querySelectorAll('.cfgSlideItem[data-side], .cfgSlideItem[data-group]').forEach(el => {
            const headline = el.querySelector('.cfgSlideItem__headline');
            if (headline) {
                headline.dataset.initialized = 'false';
            }
            el.remove();
        });
    }

    if (state.overlayEl) {
        state.overlayEl.querySelectorAll('.marker').forEach(m => m.remove());
    }

    state.sideDecals.forEach(decal => {
        if (decal.dispose) {
            decal.dispose();
        } else {
            state.scene.remove(decal);
            if (decal.geometry) decal.geometry.dispose();
            if (decal.material) {
                if (decal.material.map) decal.material.map.dispose();
                decal.material.dispose();
            }
        }
    });
    state.sideDecals.clear();

    state.groupDecals.forEach(decal => {
        if (decal.dispose) {
            decal.dispose();
        }
    });
    state.groupDecals.clear();
    state.groupDecalSettings.clear();
    state.lockedGroupDecals.clear();

    state.lockedDecals.clear();
    state.decalSettings.clear();
    state.decalMaterialsByGroup.clear();

    // Clear uploaded graphics and layers
    state.uploadedGraphics.forEach(graphic => {
        if (graphic.texture && graphic.texture.dispose) {
            graphic.texture.dispose();
        }
    });
    state.setUploadedGraphics([]);

    // Clear graphic layers (UV positions, textures)
    state.graphicLayers.forEach((layer, index) => {
        if (layer && layer.texture && layer.texture.dispose) {
            layer.texture.dispose();
        }
        state.graphicLayers[index] = null;
    });
    state.graphicLayerMap.clear();

    state.originalMaterials.forEach(mats => {
        mats.forEach(m => {
            if (m.map) m.map.dispose();
            m.dispose();
        });
    });
    state.originalMaterials.clear();

    const objectsToRemove = [];
    state.scene.traverse(obj => {
        objectsToRemove.push(obj);

        if (obj.geometry) {
            obj.geometry.dispose();
        }
        if (obj.material) {
            if (Array.isArray(obj.material)) {
                obj.material.forEach(m => {
                    if (m.map) m.map.dispose();
                    if (m.normalMap) m.normalMap.dispose();
                    if (m.roughnessMap) m.roughnessMap.dispose();
                    if (m.metalnessMap) m.metalnessMap.dispose();
                    if (m.aoMap) m.aoMap.dispose();
                    m.dispose();
                });
            } else {
                if (obj.material.map) obj.material.map.dispose();
                if (obj.material.normalMap) obj.material.normalMap.dispose();
                if (obj.material.roughnessMap) obj.material.roughnessMap.dispose();
                if (obj.material.metalnessMap) obj.material.metalnessMap.dispose();
                if (obj.material.aoMap) obj.material.aoMap.dispose();
                obj.material.dispose();
            }
        }
    });

    while (state.scene.children.length > 0) {
        state.scene.remove(state.scene.children[0]);
    }

    objectsToRemove.forEach(obj => {
        obj.geometry = null;
        obj.material = null;
        obj.parent = null;
    });
    objectsToRemove.length = 0;

    state.markers.clear();
    state.sideMats.clear();
    state.sideMeshes.clear();
    state.featureGroups.clear();
    state.markerHelpers.length = 0;
    state.setPendingTexture(null);
    state.setActiveSide(null);
    state.setActiveSideKey(null);

    state.setAllMeshes([]);
    state.setOccludeTargets([]);

    // Invalidate mesh cache for decal overlays
    invalidateMeshCache();

    if (state.renderer) {
        if (state.renderer.renderLists) {
            state.renderer.renderLists.dispose();
        }
        if (state.renderer.info && state.renderer.info.programs) {
            state.renderer.info.reset();
        }
    }

    // setupEnvironment from sceneManager
    const { setupEnvironment } = require('./sceneManager.js');
    setupEnvironment();

    showLoader(loaderEl, containerEl);

    // Add timestamp to prevent aggressive caching
    const urlWithTimestamp = addCacheBustingTimestamp(newModelUrl);

    // iOS FIX: Give garbage collector time to free memory before loading new model
    const loadDelay = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 100 : 0;

    setTimeout(() => {
    new GLTFLoader(manager)
        .setDRACOLoader(dracoLoader)
        .load(urlWithTimestamp, (gltf) => {
            try {
                onModelReloaded(gltf);
            } catch (error) {
                console.error('[GLTFLoader] Error in onModelReloaded callback:', error);
                if (window.DEBUG_IOS_ERRORS) {
                    console.error('[GLTFLoader] Error: ' + error.message + '\n\nStack: ' + error.stack?.substring(0, 500));
                }
                state.setIsLoading(false);
            }
        }, undefined, err => {
            console.error('[GLTFLoader] Error loading model:', err);
            if (window.DEBUG_IOS_ERRORS) {
                console.error('[GLTFLoader] Load Error: ' + (err?.message || err));
            }
            state.setIsLoading(false);
        });
    }, loadDelay);
}

// ===== HELPER FUNCTIONS =====

export function rebuildOccludeTargets() {
    const newOccludeTargets = Array.from(state.sideMeshes.values())
        .flatMap(g => Array.from(g.values()).flatMap(s => [...s]));
    state.setOccludeTargets(newOccludeTargets);
}

/**
 * Liest das CMS-Flag "Decals auf Glas voll deckend" vom Modell-Radio (data-glass-decals-opaque)
 * und schreibt es in den State. Pro Modell konfigurierbar.
 */
export function applyGlassDecalsOpaqueFromRadio(radio) {
    const v = radio?.dataset?.glassDecalsOpaque;
    state.setGlassDecalsOpaque(v === '1' || v === 'true' || v === 'on');
}

export function initSizeSelector(reloadModelFn, skipInitialLoad = false) {
    const panelContainer = document.querySelector('.configuratorPanel');
    if (!panelContainer) return;

    const radios = panelContainer.querySelectorAll('input[name="modelSize"]');
    if (!radios.length) return;

    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Check for initial setup
    const urlParams = new URLSearchParams(window.location.search);
    const hasUuid = urlParams.has('uuid');
    const needsInitialSetup = !hasUuid && !window._radioInitialized;

    // Simple event handler for model changes
    radios.forEach((radio) => {
        const oldHandler = radio._modelChangeHandler;
        if (oldHandler) {
            radio.removeEventListener('change', oldHandler);
        }

        const newHandler = (event) => {
            // Use native radio behavior; do not prevent default on iOS
            if (radio.checked && !state.isLoading) {
                try {
                    if (window.DEBUG_IOS_ERRORS) {
                        console.log('[Radio] Switching to model:', radio.value);
                    }
                    // Read initialZoom from selected radio button
                    const initialZoomAttr = radio.dataset.initialZoom;
                    if (initialZoomAttr) {
                        const parsed = parseInt(initialZoomAttr.replace('%', ''), 10);
                        if (!isNaN(parsed) && parsed > 0 && parsed <= 200) {
                            state.setInitialZoom(parsed);
                        } else {
                            state.setInitialZoom(100);
                        }
                    } else {
                        state.setInitialZoom(100);
                    }
                    // CMS-Flag "Decals auf Glas opak" für das gewählte Modell übernehmen
                    applyGlassDecalsOpaqueFromRadio(radio);
                    reloadModelFn(radio.value);
                } catch (error) {
                    console.error('[Radio] Error switching model:', error);
                    if (window.DEBUG_IOS_ERRORS) {
                        console.error('[Radio] Error: ' + error.message + '\n\nStack: ' + (error.stack ? error.stack.substring(0, 500) : 'N/A'));
                    }
                }
            }
        };

        radio._modelChangeHandler = newHandler;
        radio.addEventListener('change', newHandler, { passive: true });

        // Do not attach extra iOS label handlers; rely on native label->input activation
        const label = radio.closest('label');
        if (label && !label._labelHandlerMarked) {
            label._labelHandlerMarked = true; // marker to avoid future attachment attempts after rebuilds
        }
    });

    // Initial radio button setup - ONLY set checked state, don't trigger any events
    if (needsInitialSetup) {
        const active = panelContainer.querySelector('input[name="modelSize"]:checked') || radios[0];
        if (active && !active.checked) {
            // Directly set checked property without triggering events
            Object.defineProperty(active, 'checked', {
                value: true,
                writable: true,
                configurable: true
            });
            // Force visual update
            active.setAttribute('checked', 'checked');
        }
        // Set initial zoom from the initially selected radio
        if (active) {
            const initialZoomAttr = active.dataset.initialZoom;
            if (initialZoomAttr) {
                const parsed = parseInt(initialZoomAttr.replace('%', ''), 10);
                if (!isNaN(parsed) && parsed > 0 && parsed <= 200) {
                    state.setInitialZoom(parsed);
                }
            }
            // CMS-Flag "Decals auf Glas opak" vom initial gewählten Modell übernehmen
            applyGlassDecalsOpaqueFromRadio(active);
        }
        window._radioInitialized = true;
    }
}

/**
 * Updates the checked state of a radio button safely, with iOS-specific handling
 * Used for UUID-based configuration loading where we need to switch models
 */
export function setModelRadioChecked(modelUrl, isIOS) {
    const panelContainer = document.querySelector('.configuratorPanel');
    if (!panelContainer) return;

    const radios = panelContainer.querySelectorAll('input[name="modelSize"]');
    const targetRadio = Array.from(radios).find(r => r.value === modelUrl);

    // CMS-Flag "Decals auf Glas opak" für das geladene Modell übernehmen (z.B. UUID-Konfiguration)
    if (targetRadio) {
        applyGlassDecalsOpaqueFromRadio(targetRadio);
    }

    if (targetRadio && !targetRadio.checked) {
        const applyCheck = () => {
            // Ensure visual sync: only the active radio has the checked attribute
            Array.from(radios).forEach(r => r.removeAttribute('checked'));
            targetRadio.checked = true;
            targetRadio.setAttribute('checked', 'checked');
        };

        if (isIOS) {
            // On iOS: Delay slightly to avoid gesture conflicts
            setTimeout(applyCheck, 300);
        } else {
            applyCheck();
        }
    }
}
